import { useLayoutEffect, useMemo, useRef, type CSSProperties } from "react";
import { Button, Surface } from "@/components/ui";
import { Icons } from "@/components/ui/icons";
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuTrigger,
} from "@/components/ui/context-menu";
import { useT } from "@/i18n";
import { canvasMessages } from "@/i18n/messages/canvas";
import { cn } from "@/lib/cn";
import { useCanvasTimePageStore } from "@/stores/canvas-time-page-store";
import { useProjectStore } from "@/stores/project-store";
import {
  fitCanvasTimePage,
  hideCanvasTimePage,
  selectCanvasTimePage,
} from "@/lib/canvas-time-page-actions";
import { getCanvasTimePages } from "@/lib/canvas-time-page-membership";
import { useCanvasTimePageNodes } from "./use-canvas-time-page-nodes";

interface CanvasTimePageMapProps {
  rightInset?: number;
}

export function CanvasTimePageMap({ rightInset = 16 }: CanvasTimePageMapProps) {
  const t = useT(canvasMessages);
  const pageNodes = useCanvasTimePageNodes();
  const currentSpaceId = useProjectStore(
    (state) => state.currentSpace?.id ?? null,
  );
  const activePageIndex = useCanvasTimePageStore(
    (state) => state.activePageIndex,
  );
  const pageLayout = useCanvasTimePageStore((state) => state.layout);
  const pages = useMemo(
    () => getCanvasTimePages(pageNodes, currentSpaceId, pageLayout),
    [currentSpaceId, pageNodes, pageLayout],
  );

  const mapRef = useRef<HTMLDivElement>(null);
  // Cursor position of the right-click that opened a tile's menu — the
  // "Select all in page" follow-up (multi-selection Hide/Delete) opens here.
  const contextAnchorRef = useRef<{ x: number; y: number } | null>(null);

  // Follow the canvas page grid: Recent first, then older pages row by row.
  // Keep the active tile visible when there are more than three rows.
  useLayoutEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const tile = map.querySelector<HTMLElement>(
      `[data-testid="canvas-time-page-${activePageIndex}"]`,
    );
    if (!tile) return;
    if (tile.offsetTop < map.scrollTop) {
      map.scrollTop = tile.offsetTop;
    } else if (
      tile.offsetTop + tile.offsetHeight >
      map.scrollTop + map.clientHeight
    ) {
      map.scrollTop = tile.offsetTop + tile.offsetHeight - map.clientHeight;
    }
  }, [activePageIndex, pages.length]);

  if (pages.length < 2) return null;

  return (
    <Surface
      ref={mapRef}
      surfaceRole="panel"
      nativeLevel="root"
      nativeGroup="canvas-time-map"
      className={cn(
        "pointer-events-auto absolute bottom-4 z-30 grid max-h-[194px] max-w-[calc(100vw-32px)] auto-rows-[54px] gap-1.5 overflow-x-hidden overflow-y-auto rounded-control border border-glass-stroke bg-glass-fill-heavy p-2 shadow-glass backdrop-blur-glass",
        pages.length === 2 ? "w-[220px] grid-cols-2" : "w-[326px] grid-cols-3",
      )}
      style={
        {
          right: rightInset,
          "--radius-surface-panel": "12px",
          "--denk-surface-radius-panel": "12px",
        } as CSSProperties
      }
      data-testid="canvas-time-page-map"
      data-interactive="true"
      role="navigation"
      aria-label={t("timePagesNav")}
    >
      {pages.map((page, index) => {
        const active = index === activePageIndex;
        const countLabel = t("timePageFrames", { count: page.frameCount });
        const title =
          index === 0
            ? t("timePageRecent")
            : t("timePageNumbered", { number: index + 1, label: page.label });
        return (
          <ContextMenu key={page.id}>
            <ContextMenuTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                aria-current={active ? "page" : undefined}
                aria-label={t("timePageAria", { title, frames: countLabel })}
                onClick={() => fitCanvasTimePage(index)}
                onContextMenu={(event) => {
                  contextAnchorRef.current = {
                    x: event.clientX,
                    y: event.clientY,
                  };
                }}
                style={{ borderRadius: "var(--appkit-radius-control-sm)" }}
                className={cn(
                  "h-full min-w-0 flex-col items-start justify-center gap-1 border px-2 py-1 text-left",
                  active
                    ? "border-accent bg-accent-muted text-accent"
                    : "border-glass-stroke-subtle bg-black/10 text-secondary hover:border-glass-stroke hover:bg-glass-fill-heavy hover:text-primary",
                )}
                data-testid={`canvas-time-page-${index}`}
              >
                <span className="pointer-events-none max-w-full truncate text-appkit-mini font-medium leading-none">
                  {title}
                </span>
                <span className="pointer-events-none max-w-full truncate text-appkit-mini leading-none opacity-75">
                  {countLabel}
                </span>
              </Button>
            </ContextMenuTrigger>
            <ContextMenuContent>
              <ContextMenuItem
                icon={<Icons.MousePointerClick />}
                onClick={() =>
                  selectCanvasTimePage(
                    index,
                    contextAnchorRef.current ?? undefined,
                  )
                }
                data-testid={`canvas-time-page-select-${index}`}
              >
                {t("selectAllInPage")}
              </ContextMenuItem>
              <ContextMenuItem
                icon={<Icons.EyeOff />}
                onClick={() => hideCanvasTimePage(index)}
                data-testid={`canvas-time-page-hide-${index}`}
              >
                {t("hidePage")}
              </ContextMenuItem>
            </ContextMenuContent>
          </ContextMenu>
        );
      })}
    </Surface>
  );
}
